import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, PackageCheck, PackageSearch, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ItemMappingNote } from "@/features/store-indents/ItemMappingNote";
import { reportIssueOutcome, useStoreIndent, useIssueStoreIndent, useLocationNodes, useCheckStoreIndentStock } from "@/features/store-indents/api";
import { useStoreAllocations } from "@/features/store-allocations/api";
import { ITEM_KIND_LABELS } from "@/features/store-indents/types";
import { resolveLocationPath, type LocationNode } from "@/features/store-indents/LocationHierarchyPicker";
import { ShortagePanel } from "@/features/store-indents/ShortagePanel";
import { hasPermission } from "@/lib/permissions";
import { SiApprovalPanel } from "@/features/store-indents/SiApprovalPanel";
import { storeCanProcess } from "@/features/store-indents/siApproval";
import { canCreatePurchaseIndent } from "@/lib/roles";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/store-indents/$id")({
  component: StoreIndentDetail,
});

type StockInfo = {
  // null for asset and free-text lines: there is no spare quantity to count (the API sends availability_status instead)
  total_on_hand: number | null;
  total_reserved: number | null;
  available: number | null;
  availability_status?: string;
  stores: Array<{ store_location: string; branch_id: string | null; qty_on_hand: number; qty_reserved: number; available: number }>;
};

function StoreIndentDetail() {
  const matchRoute = useMatchRoute();
  const isExact = matchRoute({ to: "/store-indents/$id", fuzzy: false });

  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useStoreIndent(id);
  const { data: locationData } = useLocationNodes();
  const issue = useIssueStoreIndent();
  const { data: me } = useCurrentUser();
  const canIssue = (me?.roles ?? []).some((r) => r === "purchase_stores" || r === "super_admin");
  const locationNodes = (locationData ?? []) as LocationNode[];

  const { data: allAllocations } = useStoreAllocations({ store_indent: id });

  const [toIssue, setToIssue] = useState<Record<string, number>>({});
  const [stock, setStock] = useState<Record<string, StockInfo>>({});
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);
  const [uncheckedCount, setUncheckedCount] = useState(0);

  useEffect(() => {
    if (data?.items) {
      const init: Record<string, number> = {};
      for (const it of data.items) {
        const pending = Number(it.qty_requested) - Number(it.qty_issued);
        // Only a line linked to a Store Item Master spare has stock to issue; free-text lines default to 0 (ISSUE-9422)
        // default Issue Now to what is actually issuable (0 for unavailable / new / free-text lines)
        const issuable = Number(data.issue_eligibility?.lines?.[it.id] ?? 0);
        init[it.id] = it.spare?.id && pending > 0 ? Math.min(pending, issuable) : 0;
      }
      setToIssue(init);
      setStock({});
    }
  }, [data?.id, JSON.stringify(data?.issue_eligibility?.lines)]); // eslint-disable-line

  // Line-level kinds (mixed documents); legacy records without item_kind fall back to the header kind.
  const lineKind = (it: { item_kind?: string }) => it.item_kind ?? data?.kind;
  const lineItems = data?.items ?? [];
  const isAsset = lineItems.length > 0 ? lineItems.every((it) => lineKind(it) === "asset" && !(it as any).is_new_item) : data?.kind === "asset";
  const hasAsset = lineItems.some((it) => lineKind(it) === "asset");
  const hasLocation = lineItems.some((it) => !!it.location_id);
  const pendingSpareItems = useMemo(
    () => (data?.items ?? []).filter((it) => (it.spare?.id ?? null) && Number(it.qty_requested) - Number(it.qty_issued) > 0),
    [data?.items],
  );

  const checkStockMutation = useCheckStoreIndentStock();

  const checkStock = async (spareId: string, itemId: string) => {
    // Left for compatibility with the row-level button, but it now checks all stock
    await checkAllStock();
  };

  const checkAllStock = async () => {
    // Every open line is checked: spares get quantities, asset / free-text lines get a status message
    const checkable = (data?.items ?? []).filter((it) => Number(it.qty_requested) - Number(it.qty_issued) > 0);
    if (checkable.length === 0 || !data?.id) return;

    // Set all pending items to checking state
    const newChecking: Record<string, boolean> = {};
    for (const it of checkable) newChecking[it.id] = true;
    setChecking((s) => ({ ...s, ...newChecking }));
    
    try {
      const results = await checkStockMutation.mutateAsync(data.id);
      
      const newStock: Record<string, StockInfo> = {};
      const newToIssue: Record<string, number> = {};
      
      for (const res of results) {
        const itemId = res.item_id;
        const info = res as StockInfo;
        newStock[itemId] = info;
        
        // Find the requested quantity for this item
        const item = pendingSpareItems.find(it => it.id === itemId);
        if (item) {
          const requested = Number(item.qty_requested) - Number(item.qty_issued);
          newToIssue[itemId] = typeof info.available === "number" ? Math.max(0, Math.min(requested, info.available)) : 0;
        }
      }
      
      setStock((s) => ({ ...s, ...newStock }));
      setToIssue((s) => ({ ...s, ...newToIssue }));
      toast.success("Stock refreshed for all lines");
      refetch();     // re-evaluate Record Issue eligibility with the fresh stock
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      // Clear checking state
      const clearedChecking: Record<string, boolean> = {};
      for (const it of checkable) clearedChecking[it.id] = false;
      setChecking((s) => ({ ...s, ...clearedChecking }));
    }
  };

  if (isLoading || !data) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const distinctKinds = new Set(lineItems.map((it) => lineKind(it)));
  const kindLabel = distinctKinds.size > 1 ? "Mixed" : isAsset ? "Asset Allocation" : ITEM_KIND_LABELS[(lineKind(lineItems[0] ?? {}) ?? data.kind) as keyof typeof ITEM_KIND_LABELS] ?? "Spare";

  const executeIssue = async () => {
    // Guard: any qty over available
    const overIssue = pendingSpareItems.find((it) => {
      const info = stock[it.id];
      const q = toIssue[it.id] ?? 0;
      return info && typeof info.available === "number" && q > info.available;
    });
    if (overIssue) return toast.error("One or more lines exceed available stock");

    // Asset lines are allocated one by one on their own screen, never via quantity issue.
    const assetItemIds = new Set(lineItems.filter((it) => lineKind(it) === "asset").map((it) => it.id));
    const lines = Object.entries(toIssue)
      .filter(([id, q]) => q > 0 && !assetItemIds.has(id))
      .map(([store_indent_item_id, qty_issued]) => ({ store_indent_item_id, qty_issued }));
    if (lines.length === 0) return toast.error("Enter quantities to issue");
    try {
      const res = await issue.mutateAsync({ storeIndentId: data.id, lines });
      reportIssueOutcome(res, isAsset ? "Asset allocated" : "Issued");
      setStock({});
      setToIssue({});
      setIssueConfirmOpen(false);
    } catch (e) { toast.error((e as Error).message); }
  };

  const onIssue = async () => {
    // Guard: require stock check for all pending spare lines
    const unchecked = pendingSpareItems.filter((it) => !stock[it.id]);
    if (unchecked.length > 0) {
      setUncheckedCount(unchecked.length);
      setIssueConfirmOpen(true);
      return;
    }
    executeIssue();
  };

  const canDoIssue = canIssue && storeCanProcess(data.status);   // pending / held / rejected SIs cannot be issued (also enforced by the API)

  if (!isExact) {
    return <Outlet />;
  }

  const returnedAllocations = (allAllocations ?? []).filter(
    (a) => a.store_indent_id === data.id && a.ack_status === "Defective"
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{data.store_indent_no}</h1>
            <Badge variant="secondary">{data.status}</Badge>
            <Badge>{kindLabel}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {data.branch?.name} · {data.department?.name}
            {data.source_store_name && ` · Source Store: ${data.source_store_name}`}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Requested by <span className="font-medium">{data.requester?.full_name || data.requester?.email || "—"}</span>
            {" · "}Raised {fmtDateTime(data.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {!isAsset && canDoIssue && pendingSpareItems.length > 0 && (
            <Button variant="outline" onClick={checkAllStock}>
              <PackageSearch className="h-4 w-4 mr-1" />Check All Stock
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate({ to: "/store-indents" })}>Back</Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item</TableHead>
              {(hasAsset || hasLocation) && <TableHead>Requested location</TableHead>}
              {!isAsset && <TableHead className="text-right">Requested</TableHead>}
              {!isAsset && <TableHead className="text-right">Issued</TableHead>}
              {!isAsset && <TableHead className="text-right">Pending</TableHead>}
              {!isAsset && <TableHead>Available</TableHead>}
              {isAsset && <TableHead>Status</TableHead>}
              <TableHead>Ack Status</TableHead>
              {canDoIssue && <TableHead className="text-right w-52">{isAsset ? "Allocate" : hasAsset ? "Allocate / Issue" : "Issue Now"}</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {(data.items ?? []).map((it) => {
                const pending = Number(it.qty_requested) - Number(it.qty_issued);
                const info = stock[it.id];
                const hasQty = !!info && typeof info.available === "number";
                const short = hasQty && pending > (info!.available as number);
                const empty = hasQty && (info!.available as number) <= 0;
                // Free-text (not in Store Item Master) lines have no stock: show why, never offer a quantity to issue
                const noStock = !it.spare?.id && !it.asset && it.mapping?.resolved_item?.kind !== "asset_category";          // free-text / new item request: nothing to issue or allocate
                const rowClass = empty ? "bg-destructive/5" : short ? "bg-amber-500/5" : "";
                
                const itemAllocations = (allAllocations ?? []).filter(a => a.store_indent_item_id === it.id);
                const hasDefective = itemAllocations.some(a => a.ack_status === "Defective");
                const allGood = itemAllocations.length > 0 && itemAllocations.every(a => a.ack_status === "Good");
                const somePending = itemAllocations.some(a => !a.ack_status);
                
                const rowAsset = lineKind(it) === "asset";
                return (
                  <TableRow key={it.id} className={rowClass}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {rowAsset
                          ? (it.asset ? `${it.asset.asset_code} · ${it.asset.name}` : it.item_name)
                          : (it.spare?.name ?? it.item_name)}
                        {(it as any).is_new_item && <Badge variant="outline" className="text-[10px]">New item request</Badge>}
                        {distinctKinds.size > 1 && lineKind(it) && <Badge variant="outline" className="text-[10px]">{ITEM_KIND_LABELS[lineKind(it) as keyof typeof ITEM_KIND_LABELS]}</Badge>}
                      </div>
                      <ItemMappingNote item={it} storeIndentId={data.id} canAct={canDoIssue} remaining={pending} />
                    </TableCell>
                    {(hasAsset || hasLocation) && <TableCell className="text-xs">{it.location_id ? resolveLocationPath(locationNodes, it.location_id) : "—"}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{Number(it.qty_requested).toFixed(2)}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{Number(it.qty_issued).toFixed(2)}</TableCell>}
                    {!isAsset && <TableCell className="text-right tabular-nums">{pending.toFixed(2)}</TableCell>}
                    {!isAsset && (
                      <TableCell>
                        {noStock ? (
                          <Badge variant="destructive" className="text-[10px]">Not available in Store</Badge>
                        ) : rowAsset ? (
                          <span className="text-muted-foreground text-xs">{info?.availability_status ?? "—"}</span>
                        ) : info && !hasQty ? (
                          <span className="text-muted-foreground text-xs">{info.availability_status ?? "Availability not applicable"}</span>
                        ) : info ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-left">
                                <Badge variant={empty ? "destructive" : short ? "secondary" : "default"}>
                                  {Number(info.available ?? 0).toFixed(2)} avail
                                </Badge>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72">
                              <div className="text-xs space-y-2">
                                <div className="flex justify-between"><span>On hand</span><b>{info.total_on_hand}</b></div>
                                <div className="flex justify-between"><span>Reserved</span><b>{info.total_reserved}</b></div>
                                <div className="flex justify-between"><span>Available</span><b>{info.available}</b></div>
                                {info.stores.length > 0 && (
                                  <>
                                    <div className="mt-2 font-medium">Per store</div>
                                    <div className="space-y-1">
                                      {info.stores.map((s, i) => (
                                        <div key={i} className="flex justify-between border-b py-1">
                                          <span className="truncate">{s.store_location}</span>
                                          <span>{s.available}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : it.spare?.id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={checking[it.id]}
                            onClick={() => checkStock(it.spare!.id, it.id)}
                          >
                            {checking[it.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageSearch className="h-3 w-3 mr-1" />}
                            Check
                          </Button>
                        ) : (
                          <button 
                            type="button"
                            className="text-muted-foreground hover:text-foreground flex items-center text-xs"
                            onClick={() => it.spare?.id && checkStock(it.spare.id, it.id)}
                            disabled={checking[it.id]}
                          >
                            {checking[it.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PackageSearch className="h-3 w-3 mr-1" />}
                            Check
                          </button>
                        )}
                      </TableCell>
                    )}
                    {isAsset && (
                      <TableCell>
                        <Badge variant={pending > 0 ? "secondary" : "default"}>{pending > 0 ? "Pending" : "Allocated"}</Badge>
                      </TableCell>
                    )}
                    <TableCell>
                      {hasDefective ? <Badge variant="destructive">Returned</Badge>
                        : allGood ? <Badge variant="default" className="bg-green-600 hover:bg-green-700">Accepted</Badge>
                        : itemAllocations.length > 0 && somePending ? <Badge variant="secondary">Pending Ack</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    {canDoIssue && (
                      <TableCell className="text-right">
                        {rowAsset && !noStock ? (
                          pending <= 0 || issue.isPending ? (
                            <Button size="sm" disabled>Allocate</Button>
                          ) : (
                            <Button size="sm" asChild>
                              <Link to="/store-indents/$id/allocate/$itemId" params={{ id: data.id, itemId: it.id }}>
                                Allocate
                              </Link>
                            </Button>
                          )
                        ) : (
                          <Input type="number" step="1" min={0} max={noStock ? 0 : Math.min(hasQty ? (info!.available as number) : pending, Number(data.issue_eligibility?.lines?.[it.id] ?? pending))}
                            disabled={noStock}
                            title={noStock ? "Not available in Store - link it to a Store Item or raise a Purchase Indent for the shortage" : undefined}
                            value={noStock ? 0 : toIssue[it.id] !== undefined ? toIssue[it.id] : ""}
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e" || e.key === "." || e.key === ",") e.preventDefault();
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                setToIssue((s) => ({ ...s, [it.id]: "" as any }));
                                return;
                              }
                              const parsed = parseInt(val, 10);
                              if (!Number.isNaN(parsed)) {
                                setToIssue((s) => ({ ...s, [it.id]: Math.max(0, parsed) }));
                              }
                            }}
                            className="w-28 ml-auto" />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {hasPermission(me, "view_store_indent_shortage") && <ShortagePanel storeIndentId={data.id} canCreate={canCreatePurchaseIndent(me?.roles) && storeCanProcess(data.status)} />}
      <SiApprovalPanel id={data.id} isRequester={data.requester?.id === me?.user?.id} />

      {canDoIssue && !isAsset && (
        <div className="flex justify-end">
          <Button onClick={onIssue} disabled={issue.isPending || !data.issue_eligibility?.can_issue}><PackageCheck className="h-4 w-4 mr-1" />Record Issue</Button>
        </div>
      )}
      {canDoIssue && !isAsset && data.issue_eligibility && !data.issue_eligibility.can_issue && (
        <p className="text-right text-sm text-muted-foreground">
          {data.issue_eligibility.reason?.startsWith("No items") ? data.issue_eligibility.reason : (data.issue_eligibility.reason ?? "")}
        </p>
      )}

      {returnedAllocations.length > 0 && (
        <Card className="border-destructive">
          <CardHeader className="bg-destructive/10 pb-4">
            <CardTitle className="text-destructive">Returned Items</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {returnedAllocations.map((a) => {
                const name = a.item_name || a.asset?.name || a.spare?.name || "Unknown Item";
                return (
                  <div key={a.id} className="border rounded-md p-4 space-y-3 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-destructive flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" /> {name}
                        </div>
                        <Badge variant="outline" className="border-destructive text-destructive">
                          Qty Returned: {a.qty_allocated}
                        </Badge>
                        <Badge variant="secondary">{a.inspection_status || "Pending Inspection"}</Badge>
                        {a.linked_ticket && (
                          <Button variant="link" size="sm" asChild className="h-6 px-2 py-0 text-xs">
                            <Link to="/tickets/$ticketId" params={{ ticketId: a.linked_ticket }}>View Ticket</Link>
                          </Button>
                        )}
                      </div>
                      {canDoIssue && (
                        <Button size="sm" variant="outline" asChild>
                          <Link to="/store-returns" search={{ indentId: data.id }}>Inspect Item</Link>
                        </Button>
                      )}
                    </div>
                    {a.ack_remarks && (
                      <div>
                        <div className="text-xs text-muted-foreground font-medium mb-1">Remarks</div>
                        <p className="text-sm bg-background p-2 rounded border">{a.ack_remarks}</p>
                      </div>
                    )}
                    {Array.isArray(a.ack_attachments) && a.ack_attachments.length > 0 && (
                      <div>
                        <div className="text-xs text-muted-foreground font-medium mb-2">Attachments</div>
                        <div className="flex flex-wrap gap-2">
                          {a.ack_attachments.map((path, i) => {
                            const url = path.startsWith("http") || path.startsWith("/") ? path : `/media/${path}`;
                            return (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="block w-24 h-24 border rounded overflow-hidden hover:opacity-80 transition-opacity bg-background"
                              >
                                <img
                                  src={url}
                                  alt={`Attachment ${i + 1}`}
                                  className="object-cover w-full h-full"
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={issueConfirmOpen} onOpenChange={setIssueConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unchecked Stock</AlertDialogTitle>
            <AlertDialogDescription>
              {uncheckedCount} line(s) have not been stock-checked. Are you sure you want to continue with recording the issue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeIssue}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
